import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Button } from '../../components/ui/Button';
import { Skeleton } from 'primereact/skeleton';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { MultiSelect } from 'primereact/multiselect';
import TableQrGenerator from './TableQrGenerator';
import GreeterScreens from './GreeterScreens';
import LtyStepper from './LtyStepper';
import PublicLinkRow from './PublicLinkRow';
import { prettyUrl, prettyHost } from './publicUrl';
import { LOYALTY_CURRENCIES } from './currencies';

import {
  useLoyaltySettings,
  useUpdateLoyaltySettings,
  useVenueCode,
  useUpdateVenueCode,
  useTvFeaturedShow,
  useTvFeaturedHide,
  useTvWelcomeTest,
  useTvCelebrationTest,
  useFeaturedItems,
} from '../../queries/loyalty';
import QrCard from './QrCard';
import LoyaltyNav from './LoyaltyNav';
import '../../styles/honey-ink.css';
import { useInHub } from '../../nav/useInHub';

const CODE_RE = /^[a-z0-9]([a-z0-9-]{1,28})[a-z0-9]$/;

/* ── Visit-milestone map ⇄ editable rows ────────────────────────────────────
 * The API speaks {visits: bonus}; the editor works on an ordered array so a row
 * keeps its identity while its visit count is being typed. Both directions drop
 * anything non-positive — the same rule the backend applies — so what the owner
 * sees saved is exactly what gets stored. */
function mapToRows(map) {
  return Object.entries(map || {})
    .map(([visits, bonus]) => ({ visits: Number(visits), bonus: Number(bonus) }))
    .filter((r) => r.visits > 0 && r.bonus > 0)
    .sort((a, b) => a.visits - b.visits);
}

function rowsToMap(rows) {
  const out = {};
  rows.filter((r) => r.visits > 0 && r.bonus > 0)
    .sort((a, b) => a.visits - b.visits)
    .forEach((r) => { out[r.visits] = r.bonus; });
  // An empty map means "no tiers", which the API stores as NULL = off. Send null
  // outright so the intent is unambiguous rather than relying on {} coercion.
  return Object.keys(out).length ? out : null;
}

/** Visit counts appearing on more than one row — they'd collapse on save. */
function duplicateVisits(rows) {
  const seen = new Set(); const dupes = new Set();
  rows.forEach((r) => { if (seen.has(r.visits)) dupes.add(r.visits); seen.add(r.visits); });
  return dupes;
}

/** A sensible visit count for a newly added tier: past the current highest. */
function nextVisits(rows) {
  return rows.length ? Math.max(...rows.map((r) => r.visits)) + 25 : 10;
}

export default function LoyaltySettings() {
  const { t } = useTranslation();
  const inHub = useInHub();

  const { data: settings, isLoading } = useLoyaltySettings();
  const saveSettings = useUpdateLoyaltySettings();
  const { data: venue } = useVenueCode();
  const saveCode = useUpdateVenueCode();
  const featuredShow = useTvFeaturedShow();
  const featuredHide = useTvFeaturedHide();
  const welcomeTest = useTvWelcomeTest();
  const celebrationTest = useTvCelebrationTest();
  const { data: featuredItems } = useFeaturedItems();
  const companyName = useSelector((s) => s.global?.companyDetails?.company?.name) || '';

  // The rotation picker names items, so it needs the venue's featured list.
  // Hidden items are offered too: the subset is a lasting preference, and
  // dropping an item from the picker the moment it was toggled off would
  // silently rewrite the owner's choice. The resolver already refuses to put
  // an inactive item on a screen, so an inactive pick is simply inert.
  const featuredOptions = useMemo(
    () => (featuredItems || []).map((i) => ({
      value: i.id,
      label: i.active ? i.name : `${i.name} — ${t('loyalty.hidden')}`,
    })),
    [featuredItems, t],
  );

  const [form, setForm] = useState(null);
  const [code, setCode] = useState('');
  // Accordion state — first section open, the rest collapsed. Each toggles
  // independently so an owner can have several open at once.
  const [open, setOpen] = useState({ points: true, service: false, tv: false, qr: false });
  const toggle = (k) => setOpen((s) => ({ ...s, [k]: !s[k] }));

  // ── The two opt-in points controls ────────────────────────────────────────
  // Both are OFF unless the venue has explicitly configured them — the same
  // posture the backend takes (rate 0 = flat, visit_milestones NULL = no
  // bonuses), so nothing starts paying out because someone opened this screen.
  const [spendOn, setSpendOn] = useState(false);
  const [milestonesOn, setMilestonesOn] = useState(false);
  // Milestones edit as an ordered row list; the API speaks {visits: bonus}.
  // A map is miserable to edit directly — renaming a key mid-typing loses focus
  // and two rows can transiently collide on the same visit count.
  const [msRows, setMsRows] = useState([]);

  useEffect(() => {
    if (!settings) return;
    setForm({ ...settings });
    setSpendOn(Number(settings.points_per_currency_unit) > 0);
    setMilestonesOn(!!settings.visit_milestones_enabled);
    setMsRows(mapToRows(settings.visit_milestones));
  }, [settings]);

  useEffect(() => { if (venue?.venue_code) setCode(venue.venue_code); }, [venue]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const normalizedCode = (code || '').toLowerCase().trim();
  const codeValid = CODE_RE.test(normalizedCode);
  const codeDirty = normalizedCode !== (venue?.venue_code || '');

  const serviceOptions = [
    { label: `🥡 ${t('loyalty.service_counter')}`, value: 'counter' },
    { label: `🍽️ ${t('loyalty.service_table')}`, value: 'table' },
  ];

  // WHO fires a milestone cheer. Only the two live modes are offered here —
  // 'off' is a third state of automation_settings.modes['milestone'] that also
  // silences the owner's Suggestions feed, and it is not this page's to set.
  // Turning the switch above off is how you stop cheers reaching the screens.
  const celebrationModeOptions = [
    { label: `🧑‍💼 ${t('loyalty.tv.celebration_mode_suggest')}`, value: 'suggest' },
    { label: `⚡ ${t('loyalty.tv.celebration_mode_auto')}`, value: 'auto' },
  ];

  const currencySymbol = form?.currency_symbol || form?.currency_code || '';

  // Both opt-in controls are collapsed back to their OFF value when the toggle
  // is off, so flipping a switch off and saving actually turns the feature off
  // rather than leaving the last-configured value silently live on the backend.
  const buildPayload = () => ({
    ...form,
    points_per_currency_unit: spendOn ? Number(form.points_per_currency_unit) || 0 : 0,
    visit_milestones: milestonesOn ? rowsToMap(msRows) : null,
  });

  const saveBtn = (
    <Button className="lty-cta" label={t('loyalty.save')} icon="pi pi-check"
      loading={saveSettings.isLoading} onClick={() => saveSettings.mutate(buildPayload())} />
  );

  const addMsRow = () => setMsRows((r) => [...r, { visits: nextVisits(r), bonus: 50 }]);
  const setMsRow = (i, patch) => setMsRows((r) => r.map((row, n) => (n === i ? { ...row, ...patch } : row)));
  const delMsRow = (i) => setMsRows((r) => r.filter((_, n) => n !== i));
  const useRecommended = () => setMsRows(mapToRows(form?.visit_milestones_defaults));

  // Two rows on the same visit count would silently collapse into one on save
  // (object keys), so flag the duplicates rather than lose one without a word.
  const msDupes = duplicateVisits(msRows);

  return (
    <div className="lty-app lty-page p-4 md:p-6">
      <LoyaltyNav />
      {!inHub && (
      <div className="lty-head">
        <div className="lty-head-icon"><i className="pi pi-cog" /></div>
        <div>
          <div className="lty-title">{t('loyalty.settings_title')}</div>
          <div className="lty-sub">{t('loyalty.settings_sub')}</div>
        </div>
      </div>
      )}

      <div className="lty-onboard"><span className="ic">💡</span><span className="tx">{t('loyalty.onboard_settings')}</span></div>

      <div className="lty-settings-grid">
        {/* ── Guest link + QR — FIXED aside on desktop (sticky), TOP on mobile ── */}
        <aside className="lty-settings-aside">
          <Card>
            <div className="lty-card-title">{t('loyalty.venue_link')}</div>
            <div className="lty-card-sub">{t('loyalty.venue_link_sub')}</div>

            <div className="flex flex-col gap-3">
              <div className="lty-link-row">
                <span className="lty-link-prefix">{prettyHost()}/v/</span>
                <InputText value={code} onChange={(e) => setCode(e.target.value)} placeholder="my-cafe"
                  className={!codeValid && normalizedCode ? 'p-invalid' : ''} style={{ maxWidth: 200 }} />
              </div>
              {!codeValid && normalizedCode !== '' && (
                <small style={{ color: 'var(--lty-coral)' }}>{t('loyalty.venue_code_rule')}</small>
              )}
              <div className="lty-link-preview">
                {t('loyalty.preview')}: <b>{prettyUrl(`/v/${normalizedCode || '…'}`)}</b>
              </div>
              <div>
                <Button label={t('loyalty.update_link')} icon="pi pi-link" outlined
                  disabled={!codeValid || !codeDirty} loading={saveCode.isLoading}
                  onClick={() => saveCode.mutate(normalizedCode)} />
              </div>
            </div>

            <hr className="lty-divider" />

            {venue?.venue_code ? <QrCard code={venue.venue_code} /> : <Skeleton height="16rem" />}
          </Card>
        </aside>

        {/* ── Accordion sections — the long form, collapsed ── */}
        <div className="lty-settings-main">
          {isLoading || !form ? (
            <>
              <Skeleton height="4rem" className="mb-3" />
              <Skeleton height="4rem" className="mb-3" />
              <Skeleton height="4rem" className="mb-3" />
              <Skeleton height="4rem" />
            </>
          ) : (
            <>
              {/* 🎯 Points & Rewards */}
              <AccordionCard emoji="🎯" title={t('loyalty.acc.points_title')} sub={t('loyalty.acc.points_sub')}
                open={open.points} onToggle={() => toggle('points')}>
                <div className="flex flex-col gap-6">
                  <Field label={t('loyalty.points_per_visit')} hint={t('loyalty.points_per_visit_hint')}>
                    <LtyStepper value={form.points_per_visit} onChange={(v) => set('points_per_visit', v)} />
                  </Field>
                  <Field label={t('loyalty.points_per_order')} hint={t('loyalty.points_per_order_hint')}>
                    <LtyStepper value={form.points_per_order} onChange={(v) => set('points_per_order', v)} />
                  </Field>
                  <Field label={t('loyalty.welcome_bonus')} hint={t('loyalty.welcome_bonus_hint')}>
                    <LtyStepper value={form.welcome_bonus} onChange={(v) => set('welcome_bonus', v)} />
                  </Field>
                  <Field label={t('loyalty.points_per_spin')} hint={t('loyalty.points_per_spin_hint')}>
                    <LtyStepper value={form.points_per_spin} onChange={(v) => set('points_per_spin', v)} />
                  </Field>
                  <Field label={t('loyalty.points_per_birthday')} hint={t('loyalty.points_per_birthday_hint')}>
                    <LtyStepper value={form.points_per_birthday} onChange={(v) => set('points_per_birthday', v)} />
                  </Field>
                  <Field label={t('loyalty.currency')} hint={t('loyalty.currency_hint')}>
                    <Dropdown
                      value={form.currency_code}
                      onChange={(e) => set('currency_code', e.value)}
                      options={LOYALTY_CURRENCIES.map((c) => ({
                        label: `${c.flag}  ${c.code} (${c.symbol}) — ${c.name}`,
                        value: c.code,
                      }))}
                      className="w-full"
                      placeholder={t('loyalty.currency')}
                    />
                  </Field>

                  <hr className="lty-divider" />

                  {/* 💸 Spend-scaling — order points follow the bill instead of a flat rate */}
                  <ToggleRow emoji="💸" title={t('loyalty.spend.title')} desc={t('loyalty.spend.desc')}
                    checked={spendOn}
                    onToggle={(v) => {
                      setSpendOn(v);
                      // Land on a sensible 1-point-per-unit when switching on from
                      // zero, so the owner sees a working rate rather than an
                      // enabled toggle that still behaves exactly like flat mode.
                      if (v && !(Number(form.points_per_currency_unit) > 0)) set('points_per_currency_unit', 1);
                    }}>
                    {spendOn && (
                      <>
                        <Field label={t('loyalty.spend.rate_label', { currency: currencySymbol })}
                          hint={t('loyalty.spend.rate_hint')}>
                          <LtyStepper value={form.points_per_currency_unit} min={0} max={10000} step={0.5}
                            onChange={(v) => set('points_per_currency_unit', v)} />
                        </Field>
                        <div className="lty-link-preview">
                          {t('loyalty.spend.example', {
                            amount: 20,
                            currency: currencySymbol,
                            points: Math.max(
                              Number(form.points_per_order) || 0,
                              Math.floor(20 * (Number(form.points_per_currency_unit) || 0)),
                            ),
                          })}
                        </div>
                        <div className="lty-hint">{t('loyalty.spend.floor_note', { points: form.points_per_order })}</div>
                      </>
                    )}
                  </ToggleRow>

                  <hr className="lty-divider" />

                  {/* 🏅 Visit milestones — a bonus on the Nth visit, stacked on visit points */}
                  <ToggleRow emoji="🏅" title={t('loyalty.milestones.title')} desc={t('loyalty.milestones.desc')}
                    checked={milestonesOn}
                    onToggle={(v) => {
                      setMilestonesOn(v);
                      // Turning it on with nothing configured would show an empty
                      // list and save as "off" — seed the recommended tiers.
                      if (v && msRows.length === 0) setMsRows(mapToRows(form.visit_milestones_defaults));
                    }}>
                    {milestonesOn && (
                      <>
                        <div className="lty-ms-list">
                          {msRows.map((row, i) => (
                            <div className={`lty-ms-row${msDupes.has(row.visits) ? ' dupe' : ''}`} key={i}>
                              <span className="lty-ms-badge">🏅</span>
                              <div className="lty-ms-cell">
                                <label className="lty-ms-cap">{t('loyalty.milestones.visit_col')}</label>
                                <LtyStepper value={row.visits} min={1} max={100000}
                                  onChange={(v) => setMsRow(i, { visits: v })} />
                              </div>
                              <i className="pi pi-arrow-right lty-ms-arrow" aria-hidden="true" />
                              <div className="lty-ms-cell">
                                <label className="lty-ms-cap">{t('loyalty.milestones.bonus_col')}</label>
                                <LtyStepper value={row.bonus} min={1} max={100000}
                                  onChange={(v) => setMsRow(i, { bonus: v })} />
                              </div>
                              <Button type="button" text rounded severity="secondary" icon="pi pi-trash"
                                aria-label={t('loyalty.milestones.remove')} onClick={() => delMsRow(i)} />
                            </div>
                          ))}
                        </div>

                        {msRows.length === 0 && (
                          <div className="lty-onboard"><span className="ic">🏅</span>
                            <span className="tx">{t('loyalty.milestones.empty')}</span></div>
                        )}
                        {msDupes.size > 0 && (
                          <small style={{ color: 'var(--lty-coral)' }}>{t('loyalty.milestones.dupe_warn')}</small>
                        )}

                        <div className="flex gap-2 flex-wrap">
                          <Button size="small" outlined icon="pi pi-plus" label={t('loyalty.milestones.add')}
                            onClick={addMsRow} />
                          <Button size="small" text icon="pi pi-sparkles" label={t('loyalty.milestones.use_recommended')}
                            onClick={useRecommended} />
                        </div>
                        <div className="lty-hint">{t('loyalty.milestones.stack_note', { points: form.points_per_visit })}</div>
                      </>
                    )}
                  </ToggleRow>

                  {saveBtn}
                </div>
              </AccordionCard>

              {/* 🍽️ Service & Kitchen */}
              <AccordionCard emoji="🍽️" title={t('loyalty.acc.service_title')} sub={t('loyalty.acc.service_sub')}
                open={open.service} onToggle={() => toggle('service')}>
                <div className="flex flex-col gap-6">
                  <Field label={t('loyalty.service_type')} hint={t('loyalty.service_type_hint')}>
                    <LtyToggle value={form.service_type} options={serviceOptions} onChange={(v) => set('service_type', v)} />
                  </Field>
                  <Field label={t('loyalty.kitchen_pin')} hint={t('loyalty.kitchen_pin_hint')}>
                    <InputText
                      value={form.kitchen_pin || ''}
                      onChange={(e) => set('kitchen_pin', e.target.value.replace(/\D/g, '').slice(0, 8))}
                      placeholder="1234"
                      inputMode="numeric"
                      style={{ maxWidth: 160, letterSpacing: '0.3em', fontFamily: 'ui-monospace, monospace' }}
                    />
                    {/* Both surfaces run on OTHER devices — a tablet at the pass, a
                        screen at the till — so each row is openable here (to check the
                        PIN gate) and copyable (to send it where it actually lives). */}
                    {venue?.venue_code && form.kitchen_pin ? (
                      <>
                        <PublicLinkRow label={t('loyalty.kitchen_link')}
                          path={`/kitchen/${venue.venue_code}`} />
                        <PublicLinkRow label={t('loyalty.cashier_link')}
                          path={`/cashier/${venue.venue_code}`} />
                      </>
                    ) : null}
                  </Field>
                  {saveBtn}
                </div>
              </AccordionCard>

              {/* 📺 TV Screens */}
              <AccordionCard emoji="📺" title={t('loyalty.tv.title')} sub={t('loyalty.tv.sub')}
                open={open.tv} onToggle={() => toggle('tv')}>
                <div className="flex flex-col gap-5">
                  <ToggleRow emoji="🍽️" title={t('loyalty.tv.featured_title')} desc={t('loyalty.tv.featured_desc')}
                    checked={!!form.tv_featured_enabled} onToggle={(v) => set('tv_featured_enabled', v)}>
                    {form.tv_featured_enabled && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label={t('loyalty.tv.dwell')} hint={t('loyalty.tv.dwell_hint')}>
                          <LtyStepper value={form.tv_featured_dwell_seconds ?? 20} min={8} max={120}
                            onChange={(v) => set('tv_featured_dwell_seconds', v)} />
                        </Field>
                        {/* Empty selection = every eligible item, which is why the
                            placeholder states that outright: an empty picker
                            otherwise reads as "nothing will show". */}
                        <Field label={t('loyalty.tv.rotation_items')} hint={t('loyalty.tv.rotation_items_hint')}>
                          <MultiSelect
                            value={form.tv_featured_rotation_ids ?? []}
                            options={featuredOptions}
                            optionLabel="label" optionValue="value"
                            display="chip" showClear
                            placeholder={t('loyalty.tv.rotation_all')}
                            emptyMessage={t('loyalty.no_items')}
                            className="w-full"
                            onChange={(e) => set('tv_featured_rotation_ids', e.value ?? [])}
                          />
                        </Field>
                        {/* F-13 follow-on: the item NAME always renders both
                            languages; the DESCRIPTION had only one slot and it
                            silently always went to English whenever filled in —
                            inherited from the prototype's own English-only demo
                            copy, never a deliberate choice for this market. This
                            is that choice, made real and changeable — default
                            Arabic, because the home market is. */}
                        <Field label={t('loyalty.tv.content_language')} hint={t('loyalty.tv.content_language_hint')}>
                          <LtyToggle
                            value={form.content_language || 'ar'}
                            options={[
                              { label: `🇯🇴 ${t('loyalty.tv.content_language_ar')}`, value: 'ar' },
                              { label: `🇬🇧 ${t('loyalty.tv.content_language_en')}`, value: 'en' },
                            ]}
                            onChange={(v) => set('content_language', v)}
                          />
                        </Field>
                      </div>
                    )}
                    <div className="flex gap-2 flex-wrap">
                      <Button size="small" outlined icon="pi pi-desktop" label={t('loyalty.tv.show_now')}
                        loading={featuredShow.isLoading} onClick={() => featuredShow.mutate(undefined)} />
                      <Button size="small" text severity="secondary" icon="pi pi-eye-slash" label={t('loyalty.tv.hide')}
                        loading={featuredHide.isLoading} onClick={() => featuredHide.mutate()} />
                    </div>
                  </ToggleRow>

                  <hr className="lty-divider" />

                  <ToggleRow emoji="🎁" title={t('loyalty.tv.welcome_title')} desc={t('loyalty.tv.welcome_desc')}
                    checked={!!form.tv_welcome_enabled} onToggle={(v) => set('tv_welcome_enabled', v)}>
                    {form.tv_welcome_enabled && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label={t('loyalty.tv.interval')} hint={t('loyalty.tv.interval_hint')}>
                          <LtyStepper value={form.tv_welcome_interval_minutes} min={5} max={1440}
                            onChange={(v) => set('tv_welcome_interval_minutes', v)} />
                        </Field>
                        <Field label={t('loyalty.tv.duration')} hint={t('loyalty.tv.duration_hint')}>
                          <LtyStepper value={form.tv_welcome_duration_minutes} min={1} max={30}
                            onChange={(v) => set('tv_welcome_duration_minutes', v)} />
                        </Field>
                      </div>
                    )}
                    <div>
                      <Button size="small" outlined icon="pi pi-send" label={t('loyalty.tv.preview')}
                        loading={welcomeTest.isLoading} onClick={() => welcomeTest.mutate()} />
                    </div>
                  </ToggleRow>

                  <hr className="lty-divider" />

                  <ToggleRow emoji="🔔" title={t('loyalty.tv.ready_title')} desc={t('loyalty.tv.ready_desc')}
                    checked={form.tv_order_ready_enabled ?? true} onToggle={(v) => set('tv_order_ready_enabled', v)}>
                    {(form.tv_order_ready_enabled ?? true) && (
                      <Field label={t('loyalty.tv.ready_seconds')} hint={t('loyalty.tv.ready_seconds_hint')}>
                        <LtyStepper value={form.tv_order_ready_seconds} min={5} max={60}
                          onChange={(v) => set('tv_order_ready_seconds', v)} />
                      </Field>
                    )}
                    <div className="lty-hint">{t('loyalty.tv.ready_auto_note')}</div>
                  </ToggleRow>

                  <hr className="lty-divider" />

                  {/* The REAL enable switch, mirroring Featured Item above.
                      Until this shipped the section held only a "Test
                      celebration" button, so an owner fired the test, watched
                      the overlay land on their screens, and reasonably
                      concluded the feature was on — while
                      tv_celebration_enabled was still false and no real guest
                      would ever be cheered.

                      The two controls are INDEPENDENT and the copy says so.
                      The switch decides whether a cheer reaches the SCREENS;
                      the mode decides WHO fires it. Turning the switch off
                      deliberately leaves the mode alone, because the mode also
                      drives the owner card in the Suggestions feed — a surface
                      with no TV involved. Both live in the same
                      automation_settings row the Suggestions → Settings dialog
                      writes, so the two pages can never disagree. */}
                  <ToggleRow emoji="🎉" title={t('loyalty.tv.celebration_title')} desc={t('loyalty.tv.celebration_desc')}
                    checked={!!form.tv_celebration_enabled} onToggle={(v) => set('tv_celebration_enabled', v)}>
                    {form.tv_celebration_enabled && (
                      <Field label={t('loyalty.tv.celebration_mode')} hint={t('loyalty.tv.celebration_mode_hint')}>
                        {/* Raw value, NOT coerced to 'suggest'. A venue whose
                            mode is 'off' (set in Suggestions → Settings)
                            raises no milestone at all, so no cheer can ever
                            reach the screens however this switch is set —
                            showing "Cashier taps" as active would be the very
                            same lie the Test button used to tell. Neither
                            option lights up, and the warning below says why. */}
                        <LtyToggle
                          value={form.celebration_mode}
                          options={celebrationModeOptions}
                          onChange={(v) => set('celebration_mode', v)}
                        />
                        {form.celebration_mode === 'off' && (
                          <div className="lty-hint" style={{ marginTop: 8 }}>
                            ⚠️ {t('loyalty.tv.celebration_mode_off_warning')}
                          </div>
                        )}
                      </Field>
                    )}
                    <div className="flex flex-col gap-2">
                      <div>
                        <Button size="small" outlined icon="pi pi-send" label={t('loyalty.tv.celebration_preview')}
                          loading={celebrationTest.isLoading} onClick={() => celebrationTest.mutate()} />
                      </div>
                      {/* The exact misread this section used to invite, said
                          out loud rather than left to be discovered. */}
                      {!form.tv_celebration_enabled && (
                        <div className="lty-hint">{t('loyalty.tv.celebration_preview_only')}</div>
                      )}
                    </div>
                  </ToggleRow>

                  <hr className="lty-divider" />

                  {/* P5e — the greeter screens. Deliberately NOT a ToggleRow:
                      every other control in this section is venue-wide, and a
                      single switch here would read as "greeter mode on" for the
                      whole venue. This is a role granted to ONE screen at a
                      time, so it renders as a list of screens — and it saves
                      per row immediately rather than through this section's
                      Save, because the change pushes straight to the glass and
                      that is not something to leave sitting in an unsaved
                      form. */}
                  <GreeterScreens />

                  {saveBtn}
                </div>
              </AccordionCard>

              {/* 🔗 QR & Tables */}
              <AccordionCard emoji="🔗" title={t('loyalty.acc.qr_title')} sub={t('loyalty.acc.qr_sub')}
                open={open.qr} onToggle={() => toggle('qr')}>
                {form.service_type === 'table' ? (
                  venue?.venue_code ? (
                    <>
                      {/* The count is a SAVED setting now: the same number prints
                          the QR sheet and fills the guest's table picker when
                          they arrive from an item QR with no table in the URL.
                          It therefore needs its own Save in this accordion —
                          without one, changing it here would only ever affect
                          the print run and never reach a guest. */}
                      <TableQrGenerator
                        code={venue.venue_code}
                        venueName={companyName || venue.venue_code}
                        count={form.table_count}
                        onCountChange={(v) => set('table_count', v)}
                      />
                      <div className="lty-actions" style={{ marginTop: 16 }}>{saveBtn}</div>
                    </>
                  ) : (
                    <div className="lty-onboard"><span className="ic">🔗</span>
                      <span className="tx">{t('loyalty.acc.qr_need_code')}</span></div>
                  )
                ) : (
                  <div className="lty-onboard"><span className="ic">🍽️</span>
                    <span className="tx">{t('loyalty.tableqr.counter_hint')}</span></div>
                )}
              </AccordionCard>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AccordionCard({ emoji, title, sub, open, onToggle, children }) {
  return (
    <div className={`lty-acc${open ? ' open' : ''}`}>
      <button type="button" className="lty-acc-head" onClick={onToggle} aria-expanded={open}>
        <span className="lty-acc-emoji">{emoji}</span>
        <span className="lty-acc-titles">
          <span className="lty-acc-title">{title}</span>
          {sub && <span className="lty-acc-sub">{sub}</span>}
        </span>
        <i className="pi pi-chevron-down lty-acc-chev" aria-hidden="true" />
      </button>
      <div className="lty-acc-body-wrap">
        <div className="lty-acc-body-inner">
          <div className="lty-acc-body">{children}</div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="lty-field-label">{label}</label>
      {hint && <div className="lty-hint">{hint}</div>}
      {children}
    </div>
  );
}

/** Emoji + title + description with a switch, revealing its children when on.
 *  Shared by the TV overlay rows and the two opt-in points controls. */
function ToggleRow({ emoji, title, desc, checked, onToggle, children }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <span style={{ fontSize: 22, lineHeight: 1 }}>{emoji}</span>
        <div className="flex-1">
          <div className="lty-field-label">{title}</div>
          <div className="lty-hint">{desc}</div>
        </div>
        <InputSwitch checked={!!checked} onChange={(e) => onToggle(e.value)} />
      </div>
      {children}
    </div>
  );
}

function LtyToggle({ value, options, onChange }) {
  return (
    <div className="lty-toggle">
      {options.map((o) => (
        <button
          type="button"
          key={o.value}
          className={`lty-toggle-opt${value === o.value ? ' active' : ''}`}
          onClick={() => onChange(o.value)}
          aria-pressed={value === o.value}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
